import React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import EmailIcon from "../../assets/icons/email-icon.png";
import { ForgotPassword } from "../../redux/actions/account-action-types";
import LoginSliderPanel from "../../components/sections/LoginSliderPanel";
import { FORGOT_PASSWORD_SLIDER } from "../../components/sections/forgotPasswordSliderPanel";

const ForgotPasswordPage = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	const [email, setEmail] = React.useState("");
	const [error, setError] = React.useState("");
	const [success, setSuccess] = React.useState("");
	const [isLoading, setIsLoading] = React.useState(false);

	const [resendCooldown, setResendCooldown] = React.useState(0);
	const [resendStatus, setResendStatus] = React.useState(""); // "sent" | "error" | ""
	const [isResending, setIsResending] = React.useState(false);

	const emailRef = React.useRef(null);

	const validateEmail = (value) => {
		if (!value?.trim()) return "Please enter your email address.";
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!re.test(value.trim())) return "Please enter a valid email address.";
		return "";
	};

	const handleInputChange = (e) => {
		const { value } = e.target;
		setEmail(value);
		if (error) setError("");
		if (success) setSuccess("");
	};

	const onSubmit = (e) => {
		e.preventDefault();

		setError("");
		setSuccess("");

		const validationError = validateEmail(email);
		if (validationError) {
			setError(validationError);
			return;
		}

		if (isLoading) return;
		setIsLoading(true);

		const safetyTimeout = setTimeout(() => {
			setIsLoading(false);
			setError("Request is taking too long. Please try again.");
		}, 15000);

		try {
			dispatch(
				ForgotPassword({
					// ✅ mantém a regra do teu portal atual
					data: email.trim().toUpperCase(),
					callback: (data) => {
						clearTimeout(safetyTimeout);
						setIsLoading(false);

						// alguns reducers devolvem {success:true}, outros devolvem {error:...}
						if (data?.error) {
							setError(data?.error?.message || "Could not send reset email.");
							return;
						}

						if (data?.success === false) {
							setError("Failed to send reset link. Please try again.");
							return;
						}

						setSuccess(
							"We’ve sent a reset link to your email. Didn’t receive it? Check spam/junk and try again in a few minutes.",
						);
					},
				}),
			);
		} catch (err) {
			clearTimeout(safetyTimeout);
			setIsLoading(false);
			setError("Something went wrong. Please try again.");
		}
	};

	const resendResetLink = () => {
		const validationError = validateEmail(email);
		if (validationError) {
			setResendStatus("");
			setError(validationError);
			return;
		}

		if (resendCooldown > 0 || isResending || isLoading) return;

		setError("");
		setResendStatus("");
		setIsResending(true);

		const safetyTimeout = setTimeout(() => {
			setIsResending(false);
			setResendStatus("error");
		}, 15000);

		try {
			dispatch(
				ForgotPassword({
					data: email.trim().toUpperCase(),
					callback: (data) => {
						clearTimeout(safetyTimeout);
						setIsResending(false);

						if (data?.error || data?.success === false) {
							setResendStatus("error");
							return;
						}

						setResendStatus("sent");
						setResendCooldown(30);
					},
				}),
			);
		} catch (e) {
			clearTimeout(safetyTimeout);
			setIsResending(false);
			setResendStatus("error");
		}
	};

	React.useEffect(() => {
		if (!success) emailRef.current?.focus();
	}, [success]);

	React.useEffect(() => {
		if (resendCooldown <= 0) return;
		const interval = setInterval(
			() => setResendCooldown((prev) => prev - 1),
			1000,
		);
		return () => clearInterval(interval);
	}, [resendCooldown]);

	return (
		<div>
			<div className="prospect-combo">
				<div className="container-fluid p-0">
					<div className="row m-0">
						<div className="col-xl-3 col-lg-4 col-md-4 d-none d-md-block forgetpwd-left p-0">
							<LoginSliderPanel
								promos={FORGOT_PASSWORD_SLIDER}
								title={{ white: "Ref", accent: "App" }}
								subtitle="Reset your password securely and regain access."
								compact
							/>
						</div>

						<div className="col-xl-9 col-lg-8 col-md-8 col-12 form-element px-sm-5 pt-sm-5 d-flex flex-column align-content-center justify-content-center form-container d-flex align-items-center justify-content-center">
							<div className="login-card">
								<div className="login-top-nav">
									<a href="/account/login" className="back-link">
										<i className="zmdi zmdi-arrow-left" />
										<span>Back to login</span>
									</a>
								</div>

								<div className="login-card-header">
									<h3 className="form-heading mb-2">Forgot password</h3>
									<p className="form-subheading mb-4">
										Enter the email address associated with your account
									</p>
								</div>

								{error && (
									<div className="login-alert">
										<i className="zmdi zmdi-alert-circle" />
										<span>{error}</span>
									</div>
								)}

								{success ? (
									<>
										<div className="login-alert success">
											<i className="zmdi zmdi-check-circle" />
											<span>{success}</span>
										</div>

										<div className="success-actions">
											<button
												type="button"
												className="success-primary"
												onClick={() => history.push("/account/login")}
											>
												Go to login
												<i className="zmdi zmdi-long-arrow-right ml-2" />
											</button>
										</div>

										<div className="success-secondary">
											<button
												type="button"
												className="resend-link-btn"
												onClick={resendResetLink}
												disabled={resendCooldown > 0 || isResending}
											>
												{isResending
													? "Sending..."
													: resendCooldown > 0
														? `Resend available in ${resendCooldown}s`
														: "Resend reset link"}
											</button>

											{resendStatus === "sent" && (
												<div className="resend-status success">
													<i className="zmdi zmdi-email" />
													Sent again.
												</div>
											)}

											{resendStatus === "error" && (
												<div className="resend-status error">
													<i className="zmdi zmdi-alert-circle" />
													Could not send. Try again.
												</div>
											)}
										</div>
									</>
								) : (
									<form onSubmit={onSubmit}>
										<div className="input-group mb-4">
											<div className="input-group-prepend">
												<span className="input-group-text">
													<img src={EmailIcon} alt="emailicon" />
												</span>
											</div>

											<input
												ref={emailRef}
												type="text"
												className="form-control"
												placeholder="Email"
												id="email"
												name="email"
												value={email}
												onChange={handleInputChange}
												disabled={isLoading}
											/>
										</div>

										<button
											type="submit"
											className="btn btn-primary sign-fbtn login-submit-btn"
											disabled={isLoading}
										>
											Send reset link
											{isLoading && (
												<span className="login-spinner" aria-label="loading">
													<i className="zmdi zmdi-refresh"></i>
												</span>
											)}
										</button>
									</form>
								)}

								{(isLoading || isResending) && (
									<div className="card-loading-overlay">
										<div className="card-loading-spinner">
											<i className="zmdi zmdi-refresh" />
											<div>
												{isResending
													? "Sending again..."
													: "Sending reset email..."}
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default ForgotPasswordPage;
